/**
 * fyDataCache — in-memory, session-scoped TTL cache for expensive Firestore reads.
 *
 * Cost context: full-collection scans (stockFlow for form badges, the dashboard's
 * 10-collection fetch) were re-bought on every mount — the #1 Firestore read cost.
 * This cache lets the first consumer pay once and everyone reuse for TTL minutes.
 *
 * Safety rules (do not weaken):
 * - Fetcher errors propagate to the caller exactly as without the cache, and
 *   failures are never cached.
 * - Cache bookkeeping is wrapped so a cache bug degrades to "fetch normally",
 *   never to a broken screen.
 * - Reports must NOT use this by default — they fetch live so printed/shared
 *   figures can never be stale. Only cosmetic/informational consumers
 *   (form stock badges, dashboard widgets) may opt in.
 * - ONE APPROVED EXCEPTION (owner decision 2026-08-06): Inventory Balances
 *   (`invBalances|` keys). It was re-buying the entire stockFlow history on
 *   every Generate AND on every date/location change — 204,825 reads in a
 *   single day for one company. It now caches, and pays for that with a
 *   visible "Data as of <time>" stamp (on screen AND on the printout) plus a
 *   Refresh button. Any future report added here needs the same two things:
 *   the user must always be able to see how old the figures are, and refresh
 *   them. Note a posting does NOT clear this key — freshness is TTL+Refresh.
 */

const store = new Map();

// Fetches currently in progress, keyed the same as `store`.
//
// WHY: the cache only holds a value AFTER a fetch completes. Dashboard widgets
// mount together and ask for the same key in the same tick, so every one of
// them saw "not cached yet" and started its own fetch. Observed in production:
// the scoreboard document read FIVE times on a single dashboard load (plus
// recentTx and posCounters twice each) — the shared-loader design was supposed
// to make that one read. Callers arriving while a fetch is running now wait for
// that fetch instead of starting another.
const inflight = new Map();

export const DEFAULT_TTL_MS = 30 * 60 * 1000; // 30 minutes (user decision 2026-07-16)

export const getCached = async (key, fetcher, ttlMs = DEFAULT_TTL_MS, forceRefresh = false) => {
  if (!forceRefresh) {
    try {
      const hit = store.get(key);
      if (hit && Date.now() - hit.at < ttlMs) {
        console.log(`[fyCache] HIT ${key} — 0 Firestore reads`);
        return hit.value;
      }
    } catch (_) { /* fall through to a normal fetch */ }

    // Someone is already fetching this exact key — ride along with it.
    const pending = inflight.get(key);
    if (pending) {
      console.log(`[fyCache] JOIN ${key} — reusing in-flight fetch, 0 extra reads`);
      return pending;
    }
  }

  const t0 = Date.now();
  const promise = (async () => {
    const value = await fetcher();
    try {
      store.set(key, { value, at: Date.now() });
      console.log(`[fyCache] MISS ${key} — fetched fresh in ${Date.now() - t0}ms`);
    } catch (_) { /* caching failed; the fresh value is still returned */ }
    return value;
  })();

  inflight.set(key, promise);
  try {
    return await promise;
  } finally {
    // Clear on success AND failure: a rejected fetch must never stay latched as
    // "in flight", or every later caller would inherit that same failure. The
    // identity check avoids a forceRefresh that started later being wiped by an
    // earlier fetch finishing. Errors still propagate to every joined caller and
    // are still never cached.
    if (inflight.get(key) === promise) inflight.delete(key);
  }
};

/**
 * Mutate live entries under `prefix` in place instead of dropping them.
 * `updater(value, key)` adjusts the cached value with a known delta (e.g. the
 * stock rows just posted) so the next consumer gets EXACT data at 0 reads —
 * dropping the entry would force a full re-fetch, which for the stock summary
 * is an entire-collection scan per save (the measured #2 read cost fleet-wide).
 * Safety: an updater that throws or returns false deletes that entry — the
 * worst case is the old invalidate-and-refetch behavior, never a wrong value.
 */
export const updateCached = (prefix, updater) => {
  try {
    for (const key of [...store.keys()]) {
      if (!key.startsWith(prefix)) continue;
      const hit = store.get(key);
      if (!hit) continue;
      try {
        if (updater(hit.value, key) === false) store.delete(key);
      } catch (_) {
        store.delete(key);
      }
    }
  } catch (_) { /* never let cache bookkeeping break a save */ }
};

/** Drop every entry whose key starts with `prefix` (e.g. after posting a document). */
export const invalidateCache = (prefix) => {
  try {
    for (const key of [...store.keys()]) {
      if (key.startsWith(prefix)) store.delete(key);
    }
    // Also stop callers joining a fetch that STARTED BEFORE this invalidation —
    // it is carrying pre-change data. Dropping it here means the next caller
    // begins a fresh fetch rather than inheriting the stale one.
    for (const key of [...inflight.keys()]) {
      if (key.startsWith(prefix)) inflight.delete(key);
    }
  } catch (_) { /* never let invalidation break a save */ }
};

